import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { StreamReport, VideoResponse } from '../models';
import { EnvironmentService } from './environment.service';
import { VideoReport } from '../models/videoreport';
import { PlaylistService } from './playlist.service';
import { DeviceManagementService } from './device-management.service';
import { RecommendationsService } from './recommendations.service';

@Injectable({
    providedIn: 'root'
})
export class VideoService {

    private liveCount: number = 0;

    private _nextVideoId = new BehaviorSubject<string | undefined>(undefined);

    public get nextVideoId$(): Observable<string | undefined> {
        return this._nextVideoId.asObservable();
    }

    public get nextVideoId(): string {
        return this._nextVideoId.getValue();
    }

    private _nextVideoImage = new BehaviorSubject<string | undefined>(undefined);

    public get nextVideoImage$(): Observable<string | undefined> {
        return this._nextVideoImage.asObservable();
    }

    public get nextVideoImage(): string {
        return this._nextVideoImage.getValue();
    }

    private _previousVideoId = new BehaviorSubject<string | undefined>(undefined);

    public get previousVideoId$(): Observable<string | undefined> {
        return this._previousVideoId.asObservable();
    }

    public get previousVideoId(): string {
        return this._previousVideoId.getValue();
    }

    constructor(
        private envService: EnvironmentService,
        private http: HttpClient,
        private playlistService: PlaylistService,
        private deviceManager: DeviceManagementService,
        private recommendationService: RecommendationsService) {
    }

    getVideo(mediaId: number, platform: string, recommendationId: string = undefined): Observable<VideoResponse> {
        const headers = new HttpHeaders({
            'device-identifier': this.deviceManager.deviceIdentifier
        });

        let params = new HttpParams();
        if (recommendationId) {
            params = params.set('recommendationId', recommendationId);
        }

        return this.http.get<VideoResponse>(this.envService.contentHost + '/v5/video/' + mediaId + '?platform=' + platform, { headers, params })
    }

    getFreeVideo(mediaId: number, platform: string): Observable<VideoResponse> {
        const headers = new HttpHeaders({
            'device-identifier': this.deviceManager.deviceIdentifier
        });
        return this.http.get<VideoResponse>(this.envService.contentHost + '/v5/video/' + mediaId + '/free?platform=' + platform, { headers });
    }

    reportStreamProgress(uid: string, videoId: number, streamReport: StreamReport): Observable<any> {
        return this.http.post<any>(this.envService.userHost + '/v1/videoReport/' + uid + '?platform=stv&videoId=' + videoId, streamReport)
    }

    reportMedia(mediaId: number, report: VideoReport) {
        return this.http.post<any>(`${this.envService.contentHost}/v5/video/${mediaId}/report`, report)
    }

    getReportReasons(mediaId: number) {
        return this.http.get<any>(`${this.envService.contentHost}/v5/video/${mediaId}/report`)
    }

    setLiveCount(liveCount: number) {
        this.liveCount = liveCount;
    }

    getLiveCount(): number {
        return this.liveCount;
    }

    async refreshUpNext(mediaId: number, seasonId: number, order: string, watchlist = false) {
        let media = [];
        let response: any;
        let recommendationId = localStorage.getItem('upnext-recommendation-id');
        if (watchlist) {
            media = this.playlistService.mediaList;

            if (!media || media.length < 1) {
                response = (await this.recommendationService.getUpNext(mediaId, seasonId, 20, recommendationId).toPromise());
                media = response.media
            }
        } else {
            response = (await this.recommendationService.getUpNext(mediaId, seasonId, 20, recommendationId).toPromise());
            media = response.media;
        }

        if(response.recommendationId) {
            recommendationId =  response.recommendationId;
        }

        let mediaIndex = media.findIndex(i => i.mediaId == mediaId);

        if (mediaIndex > 0) {
            this._previousVideoId.next(media[mediaIndex - 1].mediaId.toString());
        } else {
            this._previousVideoId.next(media[media.length - 1].mediaId.toString());
        }

        const mediaCopy = JSON.parse(JSON.stringify(media));
        if (mediaIndex > -1) {
            media = media.slice(mediaIndex + 1, media.length);
        }

        if (media.length === 0) {
            media = mediaCopy;
            mediaIndex = media.findIndex(i => i.mediaId == mediaId);
            media.splice(mediaIndex, 1);
        }

        if (media[0]) {
            this._nextVideoId.next(media[0].mediaId.toString());
            this._nextVideoImage.next(media[0].images?.find(f => f.type === 'thumbnail')?.url);
            localStorage.setItem('upnext-recommendation-id', recommendationId);
        } else {
            this._nextVideoId.next(undefined);
            localStorage.removeItem('upnext-recommendation-id');
        }
    }
}
